import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useAuth } from "@/hooks/useAuth";
import { createSubscribeCallback } from "@/lib/realtimeErrorHandler";

export interface Conversation {
  id: string;
  title: string;
  updated_at: string;
  automation_guide_id: string | null;
}

export interface WebSource {
  title: string;
  url: string;
  type: "official_docs" | "community" | "web";
}

export interface Source {
  id: string;
  name: string;
  type: string;
}

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  sources?: Source[];
  web_sources?: WebSource[];
  search_tier?: "official_docs" | "community" | "web_fallback";
  source_language?: "en" | "es";
  created_at: string;
}

async function fetchConversations(
  organizationId: string,
  userId: string,
): Promise<Conversation[]> {
  const { data, error } = await supabase
    .from("automation_support_conversations")
    .select("id, title, updated_at, automation_guide_id")
    .eq("organization_id", organizationId)
    .eq("user_id", userId)
    .order("updated_at", { ascending: false })
    .limit(20);

  if (error) {
    throw new Error(`Error fetching conversations: ${error.message}`);
  }

  return (data || []) as Conversation[];
}

async function fetchMessages(conversationId: string): Promise<Message[]> {
  const { data, error } = await supabase
    .from("automation_support_messages")
    .select("*")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true });

  if (error) {
    throw new Error(`Error fetching messages: ${error.message}`);
  }

  return (data || []).map((msg: Record<string, unknown>) => ({
    id: msg.id as string,
    role: msg.role as "user" | "assistant",
    content: msg.content as string,
    sources: (msg.sources as unknown as Source[]) || undefined,
    web_sources: (msg.web_sources as unknown as WebSource[]) || undefined,
    search_tier: msg.search_tier as
      | "official_docs"
      | "community"
      | "web_fallback"
      | undefined,
    source_language: msg.source_language as "en" | "es" | undefined,
    created_at: msg.created_at as string,
  }));
}

interface UseConversationsOptions {
  enabled?: boolean;
}

/**
 * Shared hook for fetching Automation Support conversations with TanStack Query caching.
 * Data is cached for 5 minutes.
 */
export function useConversations(options: UseConversationsOptions = {}) {
  const { organization } = useUserProfile();
  const { user } = useAuth();
  const orgId = organization?.id;
  const userId = user?.id;

  return useQuery({
    queryKey: ["automation-support-conversations", orgId, userId],
    queryFn: () => fetchConversations(orgId!, userId!),
    enabled: (options.enabled ?? true) && !!orgId && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

interface UseMessagesOptions {
  enabled?: boolean;
}

/**
 * Shared hook for fetching messages for a specific conversation.
 * Data is cached for 5 minutes per conversation.
 */
export function useMessages(
  conversationId: string | null,
  options: UseMessagesOptions = {},
) {
  return useQuery({
    queryKey: ["automation-support-messages", conversationId],
    queryFn: () => fetchMessages(conversationId!),
    enabled: (options.enabled ?? true) && !!conversationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get the invalidation functions for Automation Support cache.
 */
export function useInvalidateKnowledgeHub() {
  const queryClient = useQueryClient();
  const { organization } = useUserProfile();
  const { user } = useAuth();

  return {
    /**
     * Invalidate the conversations list
     */
    invalidateConversations: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "automation-support-conversations",
          organization?.id,
          user?.id,
        ],
      });
    },
    /**
     * Invalidate messages for a specific conversation
     */
    invalidateMessages: (conversationId: string) => {
      queryClient.invalidateQueries({
        queryKey: ["automation-support-messages", conversationId],
      });
    },
    /**
     * Invalidate all Automation Support caches
     */
    invalidateAll: () => {
      queryClient.invalidateQueries({
        queryKey: ["automation-support-conversations"],
      });
      queryClient.invalidateQueries({
        queryKey: ["automation-support-messages"],
      });
    },
  };
}

/**
 * Utility to invalidate Automation Support cache from outside React components.
 */
export function invalidateKnowledgeHubCache(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({
    queryKey: ["automation-support-conversations"],
  });
  queryClient.invalidateQueries({ queryKey: ["automation-support-messages"] });
}

/**
 * Hook to subscribe to realtime changes in automation support conversations.
 * Automatically invalidates the cache when conversations are deleted (e.g., during guide regeneration).
 */
export function useAutomationSupportRealtime(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const { organization } = useUserProfile();
  const { user } = useAuth();
  const orgId = organization?.id;
  const userId = user?.id;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled || !orgId || !userId) return;

    const channel = supabase
      .channel("automation-support-changes")
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "automation_support_conversations",
        },
        () => {
          // Invalidate conversations cache when any conversation is deleted
          queryClient.invalidateQueries({
            queryKey: ["automation-support-conversations", orgId, userId],
          });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "automation_support_messages",
        },
        (payload) => {
          // Invalidate messages cache for the affected conversation
          const conversationId = (payload.old as { conversation_id?: string })
            ?.conversation_id;
          if (conversationId) {
            queryClient.invalidateQueries({
              queryKey: ["automation-support-messages", conversationId],
            });
          }
        },
      )
      .subscribe(createSubscribeCallback("automation-support-changes"));

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, queryClient, orgId, userId]);
}
